import React, { Suspense, useRef, useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { PointerLockControls, OrbitControls, Html, ContactShadows } from "@react-three/drei";
import * as THREE from "three";
import { useFetch, useSiteData } from "../lib/hooks";
import { fileUrl } from "../lib/api";
import { DEFAULT_IMAGES } from "../lib/defaults";
import { ArrowLeft, Loader2 } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  componentDidCatch(e) { console.warn("3D scene error:", e); }
  render() { return this.state.err ? this.props.fallback : this.props.children; }
}

function VenueFallback({ menu = [] }) {
  const nav = useNavigate();
  const opts = [["Plan an Event", "/quote-builder"], ["Explore Menus", "/menus"], ["Corporate Food", "/corporate"], ["View Gallery", "/gallery"], ["Contact Us", "/contact"]];
  return (
    <div className="absolute inset-0 overflow-hidden">
      <img src={fileUrl(DEFAULT_IMAGES.buffet)} alt="Catering venue" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-velvet via-burgundy/80 to-burgundy/60" />
      <div className="relative h-full flex items-center justify-center p-6">
        <div className="text-center max-w-lg">
          <p className="overline !text-gold justify-center">Virtual Reception</p>
          <h2 className="font-serif text-ivory text-4xl sm:text-5xl mt-4 mb-3">Welcome to Foodie Connect.</h2>
          <p className="text-ivory/70 mb-8 text-sm">Your device is showing the guided view of our virtual catering venue. How may we assist you today?</p>
          <div className="flex flex-wrap justify-center gap-3">
            {opts.map(([label, to]) => (
              <button key={to} onClick={() => nav(to)} className="btn-outline-gold !py-2.5" data-testid={`venue-opt-${label.toLowerCase().replace(/ /g, "-")}`}>{label}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Building blocks ---------- */
function Chef({ position = [0, 0, 0], rotation = [0, 0, 0] }) {
  return (
    <group position={position} rotation={rotation}>
      <mesh position={[0, 0.6, 0]}><cylinderGeometry args={[0.22, 0.28, 1.2, 16]} /><meshStandardMaterial color="#ffffff" /></mesh>
      <mesh position={[0, 0.35, 0.18]}><boxGeometry args={[0.42, 0.5, 0.06]} /><meshStandardMaterial color="#f3ede0" /></mesh>
      <mesh position={[0, 1.35, 0]}><sphereGeometry args={[0.2, 16, 16]} /><meshStandardMaterial color="#c98a5e" /></mesh>
      <mesh position={[0, 1.6, 0]}><cylinderGeometry args={[0.19, 0.2, 0.28, 16]} /><meshStandardMaterial color="#ffffff" /></mesh>
      <mesh position={[0, 1.76, 0]}><sphereGeometry args={[0.22, 16, 16]} /><meshStandardMaterial color="#ffffff" /></mesh>
    </group>
  );
}

function FoodTray({ position, color }) {
  return (
    <group position={position}>
      <mesh><boxGeometry args={[0.7, 0.12, 0.5]} /><meshStandardMaterial color="#cda94a" metalness={0.9} roughness={0.25} /></mesh>
      <mesh position={[0, 0.11, 0]}><boxGeometry args={[0.6, 0.12, 0.4]} /><meshStandardMaterial color={color} roughness={0.6} /></mesh>
      <mesh position={[0, 0.18, 0]} scale={[1, 0.5, 1]}><sphereGeometry args={[0.22, 16, 12]} /><meshStandardMaterial color={color} roughness={0.7} /></mesh>
    </group>
  );
}

function BuffetCounter({ position = [0, 0, 0], rotation = [0, 0, 0], label, colors }) {
  return (
    <group position={position} rotation={rotation}>
      <mesh position={[0, 0.5, 0]}><boxGeometry args={[3, 1, 1]} /><meshStandardMaterial color="#3a0b12" roughness={0.5} /></mesh>
      <mesh position={[0, 1.02, 0]}><boxGeometry args={[3.1, 0.06, 1.1]} /><meshStandardMaterial color="#D4AF37" metalness={0.8} roughness={0.3} /></mesh>
      <FoodTray position={[-0.9, 1.05, 0]} color={colors[0]} />
      <FoodTray position={[0, 1.05, 0]} color={colors[1]} />
      <FoodTray position={[0.9, 1.05, 0]} color={colors[2]} />
      <Html position={[0, 1.9, 0]} center distanceFactor={10} occlude>
        <div style={labelStyle}>{label}</div>
      </Html>
    </group>
  );
}

function LiveCounter({ position, label }) {
  const flame = useRef();
  useFrame((s) => { if (flame.current) flame.current.scale.y = 1 + Math.sin(s.clock.elapsedTime * 8) * 0.25; });
  return (
    <group position={position}>
      <mesh position={[0, 0.5, 0]}><boxGeometry args={[2, 1, 1]} /><meshStandardMaterial color="#241016" roughness={0.6} /></mesh>
      <mesh position={[0, 1.02, 0]}><boxGeometry args={[2.1, 0.06, 1.1]} /><meshStandardMaterial color="#888" metalness={1} roughness={0.2} /></mesh>
      <mesh position={[0, 1.1, 0]}><cylinderGeometry args={[0.4, 0.4, 0.1, 24]} /><meshStandardMaterial color="#111" /></mesh>
      <mesh ref={flame} position={[0, 1.35, 0]}><coneGeometry args={[0.25, 0.6, 16]} /><meshStandardMaterial color="#ff7b1a" emissive="#ff5500" emissiveIntensity={2} transparent opacity={0.85} /></mesh>
      <pointLight position={[0, 1.5, 0]} color="#ff7a1a" intensity={3} distance={5} />
      <Chef position={[0, 0, -1]} rotation={[0, 0, 0]} />
      <Html position={[0, 2.1, 0]} center distanceFactor={10} occlude>
        <div style={labelStyle}>{label}</div>
      </Html>
    </group>
  );
}

function Reception({ menu }) {
  return (
    <group position={[0, 0, -2]}>
      <mesh position={[0, 0.6, 0]}><boxGeometry args={[4, 1.2, 1]} /><meshStandardMaterial color="#4A0E17" roughness={0.4} /></mesh>
      <mesh position={[0, 1.22, 0]}><boxGeometry args={[4.2, 0.08, 1.2]} /><meshStandardMaterial color="#D4AF37" metalness={0.85} roughness={0.25} /></mesh>
      <Chef position={[0, 0, -0.9]} />
      <Html position={[0, 2.6, 0]} center distanceFactor={12} occlude>
        <div style={{ ...panelStyle, width: 300 }}>
          <div style={{ color: "#D4AF37", letterSpacing: 3, fontSize: 10 }}>RECEPTION</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, margin: "4px 0" }}>Welcome to Foodie Connect</div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>How may we assist you today? Walk ahead to explore the buffet, live counters and menu wall, or visit the enquiry desk.</div>
        </div>
      </Html>
    </group>
  );
}

function MenuWall({ menu }) {
  return (
    <group position={[-9, 0, -6]} rotation={[0, Math.PI / 2, 0]}>
      <mesh position={[0, 2, 0]}><boxGeometry args={[8, 4, 0.2]} /><meshStandardMaterial color="#2E080E" roughness={0.5} /></mesh>
      <Html position={[0, 3.3, 0.2]} center distanceFactor={12}>
        <div style={{ color: "#D4AF37", fontFamily: "'Cormorant Garamond',serif", fontSize: 26, whiteSpace: "nowrap" }}>The Menu</div>
      </Html>
      <Html position={[0, 1.9, 0.2]} center distanceFactor={14}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, width: 320 }}>
          {menu.slice(0, 6).map((c) => (
            <div key={c.id} style={{ ...panelStyle, padding: "8px 10px" }}>
              <div style={{ color: "#D4AF37", fontSize: 12, fontFamily: "'Cormorant Garamond',serif" }}>{c.name}</div>
              <div style={{ fontSize: 9, opacity: 0.7 }}>{(c.dishes || []).slice(0, 3).map((d) => d.name).join(" · ")}</div>
            </div>
          ))}
        </div>
      </Html>
    </group>
  );
}

function Room() {
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[40, 60]} /><meshStandardMaterial color="#e9e2d2" roughness={0.4} metalness={0.1} />
      </mesh>
      {/* runner carpet */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, -6]}>
        <planeGeometry args={[4, 44]} /><meshStandardMaterial color="#5c1420" roughness={0.7} />
      </mesh>
      {/* walls */}
      <mesh position={[0, 4, -28]}><boxGeometry args={[40, 8, 0.4]} /><meshStandardMaterial color="#4A0E17" /></mesh>
      <mesh position={[-14, 4, -6]}><boxGeometry args={[0.4, 8, 48]} /><meshStandardMaterial color="#3a0b12" /></mesh>
      <mesh position={[14, 4, -6]}><boxGeometry args={[0.4, 8, 48]} /><meshStandardMaterial color="#3a0b12" /></mesh>
      <mesh position={[0, 8, -6]}><boxGeometry args={[40, 0.4, 48]} /><meshStandardMaterial color="#1C1210" /></mesh>
      {/* chandeliers */}
      {[-8, 0, 8].map((z, i) => (
        <group key={i} position={[0, 6.5, z * 1.4 - 4]}>
          <pointLight color="#ffd98a" intensity={2.4} distance={16} />
          <mesh><sphereGeometry args={[0.35, 16, 16]} /><meshStandardMaterial color="#D4AF37" emissive="#D4AF37" emissiveIntensity={1.4} /></mesh>
        </group>
      ))}
    </group>
  );
}

function DiningTables() {
  return [-6, 6].map((x) => [-14, -20].map((z) => (
    <group key={`${x}-${z}`} position={[x, 0, z]}>
      <mesh position={[0, 0.75, 0]}><cylinderGeometry args={[1.1, 1.1, 0.08, 24]} /><meshStandardMaterial color="#f7f3ea" /></mesh>
      <mesh position={[0, 0.4, 0]}><cylinderGeometry args={[0.15, 0.2, 0.75, 12]} /><meshStandardMaterial color="#6b4a2b" /></mesh>
      <mesh position={[0, 0.82, 0]}><cylinderGeometry args={[0.18, 0.22, 0.15, 12]} /><meshStandardMaterial color="#D4AF37" metalness={0.8} /></mesh>
    </group>
  )));
}

/* ---------- Player controller ---------- */
function Player({ dir, locked }) {
  const { camera } = useThree();
  const vel = useRef(new THREE.Vector3());
  useFrame((_, delta) => {
    const speed = 6;
    const fwd = new THREE.Vector3();
    camera.getWorldDirection(fwd); fwd.y = 0; fwd.normalize();
    const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize();
    vel.current.set(0, 0, 0);
    const d = dir.current;
    if (d.f) vel.current.add(fwd);
    if (d.b) vel.current.addScaledVector(fwd, -1);
    if (d.l) vel.current.addScaledVector(right, -1);
    if (d.r) vel.current.add(right);
    if (vel.current.lengthSq() > 0) {
      vel.current.normalize().multiplyScalar(speed * delta);
      camera.position.add(vel.current);
    }
    camera.position.y = 1.7;
    camera.position.x = THREE.MathUtils.clamp(camera.position.x, -13, 13);
    camera.position.z = THREE.MathUtils.clamp(camera.position.z, -26, 14);
  });
  return null;
}

function Scene({ menu, dir, isMobile }) {
  return (
    <>
      <ambientLight intensity={0.45} />
      <directionalLight position={[5, 10, 5]} intensity={0.6} color="#ffe9c2" />
      <fog attach="fog" args={["#2E080E", 18, 46]} />
      <Room />
      <Reception menu={menu} />
      <BuffetCounter position={[-6, 0, -8]} label="Signature Buffet" colors={["#d69a2a", "#8a2b1a", "#b5651d"]} />
      <BuffetCounter position={[6, 0, -8]} label="Royal Curries" colors={["#7a1f12", "#c9761f", "#5c3a1a"]} />
      <Chef position={[-6, 0, -6.4]} rotation={[0, Math.PI, 0]} />
      <Chef position={[6, 0, -6.4]} rotation={[0, Math.PI, 0]} />
      <LiveCounter position={[-6, 0, -16]} label="Live Tandoor" />
      <LiveCounter position={[6, 0, -16]} label="Kebab Station" />
      <MenuWall menu={menu} />
      <DiningTables />
      <ContactShadows position={[0, 0.02, -8]} opacity={0.35} scale={40} blur={2} far={10} />
      <Player dir={dir} />
    </>
  );
}

const labelStyle = {
  background: "rgba(46,8,14,0.85)", color: "#FDFBF7", padding: "5px 12px", borderRadius: 999,
  border: "1px solid rgba(212,175,55,0.5)", fontSize: 11, letterSpacing: 1.5, whiteSpace: "nowrap",
  fontFamily: "'Outfit',sans-serif", backdropFilter: "blur(4px)",
};
const panelStyle = {
  background: "rgba(46,8,14,0.9)", color: "#FDFBF7", padding: "12px 14px", borderRadius: 12,
  border: "1px solid rgba(212,175,55,0.4)", fontFamily: "'Outfit',sans-serif", textAlign: "center",
};

export default function Experience3D() {
  const { data: menu } = useFetch("/public/menu");
  const s = useSiteData();
  const navigate = useNavigate();
  const dir = useRef({ f: false, b: false, l: false, r: false });
  const [entered, setEntered] = useState(false);
  const isMobile = typeof window !== "undefined" && (window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 768);
  const [webgl] = useState(() => {
    try { const c = document.createElement("canvas"); return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl"))); }
    catch { return false; }
  });

  useEffect(() => {
    const map = { KeyW: "f", ArrowUp: "f", KeyS: "b", ArrowDown: "b", KeyA: "l", ArrowLeft: "l", KeyD: "r", ArrowRight: "r" };
    const down = (e) => { if (map[e.code]) { dir.current[map[e.code]] = true; e.preventDefault(); } };
    const up = (e) => { if (map[e.code]) dir.current[map[e.code]] = false; };
    window.addEventListener("keydown", down); window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  const btn = (key, label) => (
    <button
      onTouchStart={() => (dir.current[key] = true)} onTouchEnd={() => (dir.current[key] = false)}
      onMouseDown={() => (dir.current[key] = true)} onMouseUp={() => (dir.current[key] = false)} onMouseLeave={() => (dir.current[key] = false)}
      className="w-14 h-14 rounded-full glass border border-gold/40 text-gold font-bold text-lg active:bg-gold/20 select-none">
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-velvet z-[70]">
      {webgl ? (
      <ErrorBoundary fallback={<VenueFallback menu={menu} />}>
      <Canvas shadows camera={{ position: [0, 1.7, 10], fov: 68 }} dpr={[1, 1.5]}>
        <color attach="background" args={["#2E080E"]} />
        <Suspense fallback={null}>
          <Scene menu={menu} dir={dir} isMobile={isMobile} />
          {isMobile
            ? <OrbitControls enablePan={false} enableZoom={false} target={[0, 1.6, -2]} maxPolarAngle={Math.PI / 1.9} />
            : entered && <PointerLockControls onUnlock={() => {}} />}
        </Suspense>
      </Canvas>
      </ErrorBoundary>
      ) : <VenueFallback menu={menu} />}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 sm:p-6 pointer-events-none">
        <Link to="/" className="btn-outline-gold !py-2.5 !px-4 pointer-events-auto" data-testid="exit-3d">
          <ArrowLeft className="w-4 h-4" /> Exit Experience
        </Link>
        <div className="glass border border-gold/30 rounded-full px-4 py-2 text-ivory/80 text-xs tracking-wide">
          {isMobile ? "Drag to look · Use pad to move" : "WASD / Arrows to move · Mouse to look"}
        </div>
      </div>

      {/* Enter overlay (desktop) */}
      {!entered && !isMobile && webgl && (
        <div className="absolute inset-0 flex items-center justify-center bg-velvet/70 backdrop-blur-sm" data-testid="enter-overlay">
          <div className="text-center max-w-md px-6">
            <p className="overline !text-gold justify-center">Virtual Catering Venue</p>
            <h2 className="font-serif text-ivory text-4xl sm:text-5xl mt-4 mb-4">Step inside Foodie Connect.</h2>
            <p className="text-ivory/70 mb-8 text-sm">Walk through our premium dining hall — reception, buffets, live counters and the menu wall. Use WASD or the arrow keys to move and your mouse to look around.</p>
            <button onClick={() => setEntered(true)} className="btn-gold mx-auto" data-testid="start-3d">Enter the Venue</button>
            <div className="mt-6"><Link to="/contact" className="text-gold/80 hover:text-gold text-sm underline">Skip to enquiry desk</Link></div>
          </div>
        </div>
      )}

      {/* Bottom actions */}
      <div className="absolute bottom-5 left-0 right-0 flex items-center justify-center gap-4 px-4">
        <button onClick={() => navigate("/quote-builder")} className="btn-gold !py-2.5 pointer-events-auto" data-testid="threed-enquire">Plan an Event</button>
        <Link to="/menus" className="btn-outline-gold !py-2.5 pointer-events-auto">View Menus</Link>
      </div>

      {/* Mobile D-pad */}
      {isMobile && webgl && (
        <div className="absolute bottom-24 right-5 grid grid-cols-3 gap-2 w-48">
          <div /> <div className="flex justify-center">{btn("f", "▲")}</div> <div />
          <div className="flex justify-center">{btn("l", "◀")}</div> <div className="flex justify-center">{btn("b", "▼")}</div> <div className="flex justify-center">{btn("r", "▶")}</div>
        </div>
      )}
    </div>
  );
}
